/**
 * "Create my PPP" in the widget (clients contract section 6, strategy 2.1 and 2.4): the pure logic behind the gated
 * starter, the due line in the welcome heading, the tool-row labels, the chips under a draft and Copy as text. No React
 * and no network, so the rules are unit-tested (ppp.test.ts); GyroAssistant only renders what these return.
 *
 * What the widget can see: GET /v1/ppp/me (parsePppMe), the tool rows of the stream (a tool name, the server's summary
 * of the call, ok or not) and done.artifacts. A tool RESULT never reaches the widget (a tool_result frame is the tool
 * name and ok), so the chips the server names on a preview are derived here from what ran in the turn and from the
 * reply text (pppChipIds), by the same rules as the server's previewChips.
 */
/** The starter sentence, and the palette entry's label. */
export const PPP_STARTER = 'Create my PPP';
/** gyro-svc's per-turn PPP tools (services/gyro-svc/src/ppp/pppTypes.ts PPP_TOOL). */
export const PPP_GATHER_TOOL = 'ppp_gather';
export const PPP_DRAFT_TOOL = 'ppp_draft';
export const PPP_EDIT_TOOL = 'ppp_edit';
export const PPP_EXPORT_TOOL = 'ppp_export';
export const PPP_EVIDENCE_TOOL = 'ppp_evidence';
/** The confirmed write that creates the Outlook draft; present in /v1/status's tools only while the person is eligible. */
export const PPP_EMAIL_TOOL = 'ppp_email';
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A report id: a lower-case UUID in its strict 8-4-4-4-12 form (PPP_REPORT_PATH holds the same pattern). */
const REPORT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
function calendarDate(value) {
    if (typeof value !== 'string')
        return undefined;
    const match = CALENDAR_DATE.exec(value);
    if (!match)
        return undefined;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const check = new Date(Date.UTC(year, month - 1, day));
    return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day ? value : undefined;
}
/**
 * The answer of GET /v1/ppp/me, read defensively: null unless `enabled` and `eligible` are both booleans (a failed,
 * refused or malformed read means no starter, never a default of "on"). Every other field is optional and dropped when
 * malformed.
 */
export function parsePppMe(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    if (typeof record.enabled !== 'boolean' || typeof record.eligible !== 'boolean')
        return null;
    const nextDueOn = calendarDate(record.nextDueOn);
    const last = record.lastReport && typeof record.lastReport === 'object' ? record.lastReport : undefined;
    const lastReport = last && typeof last.id === 'string' && REPORT_ID.test(last.id) && typeof last.fileStem === 'string' && last.fileStem.length >= 1 && last.fileStem.length <= 160 && !CONTROL.test(last.fileStem)
        ? { id: last.id, fileStem: last.fileStem } : undefined;
    return {
        enabled: record.enabled,
        eligible: record.eligible,
        verified: record.verified === true,
        ...(record.cadence === 'weekly' || record.cadence === 'biweekly' ? { cadence: record.cadence } : {}),
        ...(nextDueOn ? { nextDueOn } : {}),
        ...(lastReport ? { lastReport } : {}),
    };
}
/** Whether the starter and the palette entry show: `enabled && eligible`, nothing else. */
export function pppAvailable(me) {
    return Boolean(me && me.enabled && me.eligible);
}
// ---- Tool rows ----
// The three group sentences the server's ppp_gather `summarize()` answers (gyro-svc pppTypes.ts PPP_STEP_LABELS): the
// step number and every source of the group, so the person sees what is being read.
const ORIENTATION_LABEL = 'Step 1 of 3: reading your profile, To Do, calendar (past and planned) and your last PPP';
const MAIL_LABEL = 'Step 2 of 3: reading Outlook mail (inbox, sent, keyword searches), full threads and Asana';
const TEAMS_LABEL = 'Step 3 of 3: reading Teams chats and channels, meeting transcripts, recent files, Planner, your direct reports and Copilot retrieval';
/** The server may append the period to the orientation row (`for 9/22–10/3/26`): a short run of digits, slashes and dashes only. */
const ORIENTATION_RANGE = /^Step 1 of 3: reading your profile, To Do, calendar \(past and planned\) and your last PPP for ([0-9/–—-]{3,24})$/;
/** The part of a (possibly namespaced) tool name that names the tool. */
function bareTool(tool) {
    return tool.toLowerCase().split(/__|[.:]/).filter(Boolean).at(-1) ?? '';
}
/**
 * The row label of a PPP tool, or null for any other tool. The row of `ppp_gather` is the group's: the widget never sees
 * the call's arguments, but the server's summary of the call is the group's sentence (mail, teams, or the orientation
 * group otherwise), so the label is chosen from that and is always one of the fixed sentences below, never the summary text.
 */
export function pppToolLabel(tool, summary) {
    switch (bareTool(tool)) {
        case PPP_GATHER_TOOL: {
            const text = (summary ?? '').trim();
            if (text === MAIL_LABEL)
                return MAIL_LABEL;
            if (text === TEAMS_LABEL)
                return TEAMS_LABEL;
            if (text === ORIENTATION_LABEL)
                return ORIENTATION_LABEL;
            const range = ORIENTATION_RANGE.exec(text);
            return range ? `${ORIENTATION_LABEL} for ${range[1]}` : 'Gathering for your PPP';
        }
        case PPP_DRAFT_TOOL: return 'Writing the draft';
        case PPP_EDIT_TOOL: return 'Updating the draft';
        case PPP_EXPORT_TOOL: return 'Creating the Word and PDF files';
        case PPP_EVIDENCE_TOOL: return 'Finding where that came from';
        default: return null;
    }
}
/**
 * How many turns the widget continues a PPP run by itself before it hands back to the person (owner, 2026-10-25: one
 * step). A standard run needs two or three; this is a stop for a run that never settles, not a budget the person sees.
 */
export const PPP_AUTO_CONTINUE_MAX = 8;
/**
 * Whether the widget sends "Continue my PPP" itself after a reply: the turn gathered, steps remain, the turn's calls are
 * spent (while calls remain the model continues inside the turn), the reply asked nothing and failed nothing, and the
 * run has not already been continued PPP_AUTO_CONTINUE_MAX times in a row.
 */
export function pppShouldContinue(marks, reply, continued) {
    if (!marks?.gathered || !marks.remaining || reply.error || reply.questions > 0)
        return false;
    return marks.remaining.steps > 0 && marks.remaining.calls === 0 && continued < PPP_AUTO_CONTINUE_MAX;
}
/** The marks after a tool result, or null when the result changes none (another tool, or a failure). */
export function pppMarksAfterTool(marks, tool, ok, progress) {
    if (!ok)
        return null;
    switch (bareTool(tool)) {
        case PPP_GATHER_TOOL: return { ...marks, gathered: true, ...(progress ? { remaining: { steps: progress.stepsRemaining, calls: progress.callsRemaining } } : {}) };
        case PPP_DRAFT_TOOL:
        case PPP_EDIT_TOOL: return { ...marks, drafted: true };
        case PPP_EXPORT_TOOL: return { ...marks, drafted: true, exported: true };
        default: return null;
    }
}
/** The label of each chip (contract 6.4, exactly). */
export const PPP_CHIP_LABELS = Object.freeze({
    export: 'Export Word and PDF',
    where_from: 'Where did item N come from?',
    copy_text: 'Copy as text',
    release_labelled: 'Release labelled document and re-run',
    email: 'Email it',
    open_pdf: 'Open PDF',
    continue: 'Continue my PPP',
});
/**
 * The sentence a chip sends (contract 6.4, exactly); null for Open PDF, which sends nothing. `where_from` carries the
 * item's number (the first item's, which is 1, until the person edits it: the widget puts it in the composer instead of
 * sending), and `copy_text`'s sentence is what it sends when the reply holds no labelled block to copy.
 */
export function pppChipSentence(id, itemNumber = 1) {
    switch (id) {
        case 'export': return 'Export my PPP';
        case 'where_from': return `Where did item ${Math.max(1, Math.trunc(itemNumber) || 1)} come from?`;
        case 'copy_text': return 'Copy my PPP as text';
        case 'release_labelled': return 'Release the labelled document and re-run my PPP';
        case 'email': return 'Email my PPP';
        case 'open_pdf': return null;
        case 'continue': return 'Continue my PPP';
    }
}
/** Said by the model when a labelled document was withheld from the draft (pppCoverage.ts PPP_WITHHELD_NOTE). */
const WITHHELD_NOTE = /release the labelled document/i;
/**
 * The chips under a reply, in the server's order (pppTools.ts previewChips): after a draft, Export (Open PDF and Email
 * it once exported), Where did item N come from?, Copy as text, Release labelled document; after a gather that stopped
 * without a draft, Continue my PPP. None for any other reply. ("Go deeper" retired 2026-10-07: standard reads everything.)
 */
export function pppChipIds(ctx) {
    const marks = ctx.marks;
    if (!marks)
        return [];
    if (marks.drafted) {
        return [
            ...(marks.exported ? ['open_pdf', ...(ctx.emailOffered ? ['email'] : [])] : ['export']),
            'where_from',
            'copy_text',
            ...(WITHHELD_NOTE.test(ctx.content) ? ['release_labelled'] : []),
        ];
    }
    return marks.gathered ? ['continue'] : [];
}
/**
 * Copy as text: the labelled block of an answer, from the first line starting `NAME:` through the first line after it
 * starting `COVERAGE:`, both included, in Kyle's parser-compatible spellings: `PERIOD ENDING:` becomes `WEEK ENDING:` and
 * `PLANNED THROUGH:` becomes `WEEK OF:` (the period stays in its parenthesis). The preview's `SCORECARD:` line is left
 * out, as the server's own copy form leaves it. Null when the answer holds no such block.
 */
export function pppCopyBlock(content) {
    const lines = content.split(/\r\n|\r|\n/);
    const start = lines.findIndex((line) => line.startsWith('NAME:'));
    if (start < 0)
        return null;
    const end = lines.findIndex((line, index) => index > start && line.startsWith('COVERAGE:'));
    if (end < 0)
        return null;
    return lines.slice(start, end + 1)
        .filter((line) => !line.startsWith('SCORECARD:'))
        .map((line) => line.replace(/^PERIOD ENDING:/, 'WEEK ENDING:').replace(/^PLANNED THROUGH:/, 'WEEK OF:').trimEnd())
        .join('\n');
}
// ---- The durable download ----
/**
 * The only paths downloadPppReport fetches (contract 6.5), with the report id in the strict 8-4-4-4-12 UUID form of
 * REPORT_ID: 36 characters of hex digits and dashes alone (all dashes, say) are not an id. Group 1 is the format.
 */
export const PPP_REPORT_PATH = /^\/v1\/ppp\/reports\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(docx|pdf)$/;
/** The durable path of a report's PDF or Word file, or null when the id is not a report id. */
export function pppReportPath(id, format) {
    const path = `/v1/ppp/reports/${id}.${format}`;
    return PPP_REPORT_PATH.test(path) ? path : null;
}
/**
 * The file name of a PPP download: the Content-Disposition header's (`filename*` first, then `filename`), else
 * `<fileStem>.<ext>` from /v1/ppp/me's lastReport. A name is one path segment of printable characters ending in the
 * file's own extension. Null when neither is usable.
 */
export function pppDownloadName(contentDisposition, fileStem, format) {
    const clean = (value) => {
        if (!value)
            return null;
        const name = (value.split(/[\\/]/).pop() ?? '').replace(/[\u0000-\u001f\u007f-\u009f<>:"|?*]/g, '_').trim().slice(0, 170);
        if (!name || name === '.' || name === '..')
            return null;
        return name.toLowerCase().endsWith(`.${format}`) ? name : `${name}.${format}`;
    };
    if (contentDisposition) {
        const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
        if (extended) {
            try {
                const name = clean(decodeURIComponent(extended[1].trim()));
                if (name)
                    return name;
            }
            catch { /* a malformed escape: try the plain name */ }
        }
        const plain = /filename\s*=\s*"([^"]*)"/i.exec(contentDisposition) ?? /filename\s*=\s*([^;\s]+)/i.exec(contentDisposition);
        const name = clean(plain?.[1]);
        if (name)
            return name;
    }
    return clean(fileStem);
}
//# sourceMappingURL=ppp.js.map